import _ from 'underscore';

import { CommonErrorCode, DataConnectionErrorCode } from '~commonInfra/utils/ErrorCodes';
import { Formatters, Messages } from '~src/codegen/Localize';
import { ContentActionNotification } from '~src/commonInfra/contentActions/ContentActionNotification';
import { EditableConnection } from '~src/connectivity/components/EditConnection/IEditConnectionDialog';
import {
  ITestConnectionResultReact
} from '~src/connectivity/components/EditConnection/utils/EditConnectionActionTypes';
import { DataConnections, IConnectionUpdate } from '~src/datasource/dataConnection/resources/DataConnections';
import { ICheckConnectionResult } from '~src/datasource/dataConnection/serverApi/CheckConnectionRequest';
import { IDataSourceBase, IDataSourceDetailInfo, IDataSourceInfo } from '~src/datasource/models/IDataSourceInfo';
import { DataSources } from '~src/datasource/resources/Datasources';
import { IError } from '~src/httpClient/serverApi/types/IError';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';

import { EditConnectionDialogState } from '../EditConnectionAction';


export const MAX_PORT = 65535;

// A sentinel value to mean mixed text field values
export let TextBoxMixedValues = '———';

export namespace EditConnectionReactActionUtils {
  export function makePrompt(numberOfConnections: number) {
    return Messages.editDataConnections_prompt({
      ITEM_COUNT: numberOfConnections,
      ITEM_COUNT_LOC: Formatters.formatCount(numberOfConnections)
    });
  }

  export function isMixed(value: string): boolean {
    return value === TextBoxMixedValues;
  }

  export function uniqueValueOrMixed<T>(items: T[], iterator: _.ListIterator<T, string>) {
    const values = _.map(items, iterator);
    const first = values[0];
    return _.all(values, (val) => val === first) ? first : TextBoxMixedValues;
  }

  export function uniqueValueOrUndefined<T, TResult>(items: T[], iterator: _.ListIterator<T, TResult>) {
    const values = _.map(items, iterator);
    const first = values[0];
    return _.all(values, (val) => val === first) ? first : undefined;
  }


  export async function canEmbedPassword(datasource: IDataSourceInfo): Promise<boolean> {
    if (DataSources.isPublished(datasource)) {
      const isCubeDatasource = await checkCubeDatasource(datasource);
      return !isCubeDatasource;
    } else {
      return true;
    }
  }


  export async function checkEditedConnection(
    connectionId: string,
    dialogState: EditConnectionDialogState
  ): Promise<ICheckConnectionResult> {
    const checkConnectionResult: ICheckConnectionResult | void =
      await DataConnections.checkConnection(connectionId, editConnectionDialogStateToConnectionUpdate(dialogState));
    return checkConnectionResult;
  }

  export function applyConnectionEdits(connections: EditableConnection[], dialogState: EditConnectionDialogState): Promise<IResult> {
    return updateConnections(connections, dialogState)
      .then((result: IResult) => {
        notifyResult(result, connections);
        return result;
      });
  }

  export function showNetworkType(datasource: IDataSourceBase) {
    // Don't show for Embedded datasources
    if (_.isObject(datasource.workbook)) {
      return false;
    }
    // Only show if the FF is on
    return true;
  }

  export function isPortValid(port: string): boolean {
    return !port || !!(/^\d*$/.exec(port) && parseInt(port, 10) <= MAX_PORT);
  }

  export function notifyResult(result: IResult, connections: EditableConnection[]) {

    // Edit connection does not have partial success - it is all-or-nothing
    // So, if any errors are reported, notify complete failure (with the first error message)
    if (result && result.errors && result.errors.length) {
      ContentActionNotification.displayFailureToast(errorMessage(result.errors[0]));
    } else {
      ContentActionNotification.displaySuccessToast(
        Messages.updateConnectionsSuccess({
          ITEM_COUNT: connections.length,
          ITEM_COUNT_LOC: Formatters.formatCount(connections.length)
        })
      );
    }
  }

  function updateConnections(
    connections: EditableConnection[],
    dialogState: EditConnectionDialogState
  ) {
    return DataConnections.updateConnections(connections.map(c => c.id), editConnectionDialogStateToConnectionUpdate(dialogState));
  }

  function editConnectionDialogStateToConnectionUpdate(editConnectionDialogState: EditConnectionDialogState): IConnectionUpdate {
    return {
      server: isMixed(editConnectionDialogState.serverName) ? undefined : editConnectionDialogState.serverName,
      port: isMixed(editConnectionDialogState.serverPort) ? undefined : editConnectionDialogState.serverPort,
      username: isMixed(editConnectionDialogState.username) ? undefined : editConnectionDialogState.username,
      passwordUpdate: _.isUndefined(editConnectionDialogState.isPasswordEmbedded) ? undefined : { isEmbedded: editConnectionDialogState.isPasswordEmbedded, newPassword: editConnectionDialogState.newPassword },
      usingRemoteQueryAgent: editConnectionDialogState.usingRemoteQueryAgent
    };
  }

  function updateTestConnectionResult(serverResponse: ICheckConnectionResult): ITestConnectionResultReact {
    return {
      isPending: false,
      succeeded: serverResponse.succeeded,
      messages: serverResponse.messages,
    };
  }

  function errorMessage(error: IError): string {
    switch (error.code) {
      case CommonErrorCode.DatasourceNotFound:
      case CommonErrorCode.DataConnectionNotFound:
        return Messages.updateConnectionsError_datasourceNotFound();
      case CommonErrorCode.CallingUserInsufficientPermissions:
        return Messages.updateConnectionsError_insufficientPermissions();
      case DataConnectionErrorCode.UpdateConnectionsInvalidPort:
        return Messages.updateConnectionsError_invalidPort();
      default:
        return Messages.errorUnknown();
    }
  }

  async function checkCubeDatasource(datasource: IDataSourceInfo) {
    try {
      const datasourceDetailInfo: IDataSourceDetailInfo = await DataSources.getById(datasource.id);
      return datasourceDetailInfo.isHierarchical;
    }
    catch (e) {
      return false;
    }
  }
}
